import { useState, useEffect, useRef, type FormEvent } from 'react';
import { api } from '../services/api';
import './UsuariosCRUD.css';

type TipoUsuario = 'Cliente' | 'Arquiteto' | 'Assistente';

interface Usuario {
  id: string;
  nome: string;
  email: string;
  tipo: TipoUsuario;
  ativo: boolean;
}

interface FormErrors {
  nome?: string;
  email?: string;
  senha?: string;
  tipo?: string;
}

function validarEmail(email: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

function UsuariosCRUD() {
  const [usuarios, setUsuarios] = useState<Usuario[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filtroTipo, setFiltroTipo] = useState<string>('');
  const [filtroTexto, setFiltroTexto] = useState('');
  const [confirmModal, setConfirmModal] = useState<{ usuario: Usuario; action: 'ativar' | 'desativar' | 'excluir' } | null>(null);

  // Form/modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [formNome, setFormNome] = useState('');
  const [formEmail, setFormEmail] = useState('');
  const [formSenha, setFormSenha] = useState('');
  const [formTipo, setFormTipo] = useState<string>('');
  const [formErrors, setFormErrors] = useState<FormErrors>({});
  const [editingId, setEditingId] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const modalRef = useRef<HTMLDivElement>(null);

  const fetchUsuarios = async () => {
    try {
      setLoading(true);
      const data = await api.get('/usuarios');
      setUsuarios(data);
      setError('');
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar usuários');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsuarios();
  }, []);

  const validateForm = (): boolean => {
    const errors: FormErrors = {};

    if (!formNome.trim()) {
      errors.nome = 'Nome é obrigatório';
    } else if (formNome.length > 150) {
      errors.nome = 'Nome deve ter no máximo 150 caracteres';
    }

    if (!formEmail.trim()) {
      errors.email = 'E-mail é obrigatório';
    } else if (!validarEmail(formEmail)) {
      errors.email = 'E-mail inválido';
    }

    if (!editingId) {
      if (!formSenha) {
        errors.senha = 'Senha é obrigatória';
      } else if (formSenha.length < 8) {
        errors.senha = 'Senha deve ter no mínimo 8 caracteres';
      }
    } else if (formSenha && formSenha.length < 8) {
      errors.senha = 'Senha deve ter no mínimo 8 caracteres';
    }

    if (!formTipo) {
      errors.tipo = 'Tipo é obrigatório';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    setSubmitting(true);
    setError('');

    try {
      if (editingId) {
        const payload: { nome: string; email: string; tipo: string; senha?: string } = {
          nome: formNome,
          email: formEmail,
          tipo: formTipo,
        };
        // Só envia senha se o admin digitou uma nova (em branco = manter a atual)
        if (formSenha.trim()) {
          payload.senha = formSenha;
        }
        await api.put(`/usuarios/${editingId}`, payload);
      } else {
        await api.post('/usuarios', { nome: formNome, email: formEmail, senha: formSenha, tipo: formTipo });
      }
      closeModal();
      await fetchUsuarios();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Erro ao salvar usuário');
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormNome('');
    setFormEmail('');
    setFormSenha('');
    setFormTipo('');
    setFormErrors({});
    setEditingId(null);
  };

  const openCreateModal = () => {
    resetForm();
    setModalOpen(true);
  };

  const openEditModal = (usuario: Usuario) => {
    setEditingId(usuario.id);
    setFormNome(usuario.nome);
    setFormEmail(usuario.email);
    setFormSenha('');
    setFormTipo(usuario.tipo);
    setFormErrors({});
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    resetForm();
  };

  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      closeModal();
    }
  };

  const handleToggleStatus = (usuario: Usuario) => {
    setConfirmModal({
      usuario,
      action: usuario.ativo ? 'desativar' : 'ativar',
    });
  };

  const confirmToggleStatus = async () => {
    if (!confirmModal) return;
    try {
      await api.patch(`/usuarios/${confirmModal.usuario.id}/status`, { ativo: !confirmModal.usuario.ativo });
      setConfirmModal(null);
      await fetchUsuarios();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Erro ao alterar status');
      setConfirmModal(null);
    }
  };

  const handleExcluir = (usuario: Usuario) => {
    setConfirmModal({
      usuario,
      action: 'excluir',
    });
  };

  const confirmExcluir = async () => {
    if (!confirmModal) return;
    try {
      await api.delete(`/usuarios/${confirmModal.usuario.id}`);
      setConfirmModal(null);
      await fetchUsuarios();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Erro ao excluir usuário');
      setConfirmModal(null);
    }
  };

  // Filtered users
  const filtrados = usuarios.filter((u) => {
    const matchTipo = !filtroTipo || u.tipo === filtroTipo;
    const matchTexto =
      !filtroTexto ||
      u.nome.toLowerCase().includes(filtroTexto.toLowerCase()) ||
      u.email.toLowerCase().includes(filtroTexto.toLowerCase());
    return matchTipo && matchTexto;
  });

  const getTipoBadgeClass = (tipo: TipoUsuario): string => {
    switch (tipo) {
      case 'Arquiteto': return 'usuarios-badge usuarios-badge--arquiteto';
      case 'Assistente': return 'usuarios-badge usuarios-badge--assistente';
      case 'Cliente': return 'usuarios-badge usuarios-badge--cliente';
      default: return 'usuarios-badge';
    }
  };

  if (loading) {
    return (
      <div className="usuarios-crud">
        <div className="usuarios-crud-container">
          <p style={{ color: '#c9a96e', textAlign: 'center', marginTop: '2rem' }}>Carregando...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="usuarios-crud">
      <div className="usuarios-crud-container">
        <header className="usuarios-crud-header">
          <div className="usuarios-crud-header__info">
            <h1>Gerenciar Usuários</h1>
            <p className="usuarios-crud-subtitle">Cadastre e gerencie os usuários do sistema</p>
          </div>
          <button className="usuarios-btn-novo" onClick={openCreateModal}>
            ➕ Novo Usuário
          </button>
        </header>

        {error && (
          <p style={{ color: '#ff6b6b', textAlign: 'center', margin: '1rem 0' }}>{error}</p>
        )}

        {/* Filters */}
        <div className="usuarios-filtros">
          <input
            type="text"
            placeholder="Buscar por nome ou e-mail..."
            value={filtroTexto}
            onChange={(e) => setFiltroTexto(e.target.value)}
            aria-label="Buscar usuários"
          />
          <select
            value={filtroTipo}
            onChange={(e) => setFiltroTipo(e.target.value)}
            aria-label="Filtrar por tipo"
          >
            <option value="">Todos os tipos</option>
            <option value="Cliente">Cliente</option>
            <option value="Arquiteto">Arquiteto</option>
            <option value="Assistente">Assistente</option>
          </select>
        </div>

        {/* Users Table */}
        <div className="usuarios-table-container">
          <table className="usuarios-table">
            <thead>
              <tr>
                <th>Nome</th>
                <th>E-mail</th>
                <th>Tipo</th>
                <th>Status</th>
                <th>Ações</th>
              </tr>
            </thead>
            <tbody>
              {filtrados.length === 0 ? (
                <tr>
                  <td colSpan={5} className="usuarios-table__empty">
                    Nenhum usuário encontrado.
                  </td>
                </tr>
              ) : (
                filtrados.map((usuario) => (
                  <tr key={usuario.id} className="usuarios-table__row">
                    <td className="usuarios-table__cell-name">{usuario.nome}</td>
                    <td>{usuario.email}</td>
                    <td>
                      <span className={getTipoBadgeClass(usuario.tipo)}>
                        {usuario.tipo}
                      </span>
                    </td>
                    <td>
                      <span className={`usuarios-badge-status ${usuario.ativo ? 'usuarios-badge-status--ativo' : 'usuarios-badge-status--inativo'}`}>
                        {usuario.ativo ? 'Ativo' : 'Inativo'}
                      </span>
                    </td>
                    <td>
                      <div className="usuarios-table__actions">
                        <button
                          className="usuarios-table__action-btn"
                          onClick={() => openEditModal(usuario)}
                          aria-label={`Editar ${usuario.nome}`}
                          title="Editar"
                        >
                          ✏️
                        </button>
                        <button
                          className="usuarios-table__action-btn usuarios-table__action-btn--toggle"
                          onClick={() => handleToggleStatus(usuario)}
                          aria-label={`${usuario.ativo ? 'Desativar' : 'Ativar'} ${usuario.nome}`}
                          title={usuario.ativo ? 'Desativar' : 'Ativar'}
                        >
                          {usuario.ativo ? '🚫' : '✅'}
                        </button>
                        <button
                          className="usuarios-table__action-btn usuarios-table__action-btn--excluir"
                          onClick={() => handleExcluir(usuario)}
                          aria-label={`Excluir ${usuario.nome}`}
                          title="Excluir"
                        >
                          🗑️
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Create/Edit Modal */}
        {modalOpen && (
          <div className="usuarios-modal-overlay" onClick={handleOverlayClick}>
            <div className="usuarios-modal" ref={modalRef} onClick={(e) => e.stopPropagation()}>
              <div className="usuarios-modal__header">
                <h2>{editingId ? 'Editar Usuário' : 'Novo Usuário'}</h2>
                <button className="usuarios-modal__close" onClick={closeModal} aria-label="Fechar">
                  ✕
                </button>
              </div>
              <form onSubmit={handleSubmit} noValidate>
                <div className="usuarios-modal__body">
                  <div className="form-group">
                    <label htmlFor="usuario-nome">Nome</label>
                    <input
                      id="usuario-nome"
                      type="text"
                      value={formNome}
                      onChange={(e) => setFormNome(e.target.value)}
                      maxLength={150}
                      placeholder="Nome completo"
                      className={formErrors.nome ? 'error' : ''}
                    />
                    <span className="form-error">{formErrors.nome || ''}</span>
                  </div>

                  <div className="form-group">
                    <label htmlFor="usuario-email">E-mail</label>
                    <input
                      id="usuario-email"
                      type="email"
                      value={formEmail}
                      onChange={(e) => setFormEmail(e.target.value)}
                      placeholder="email@exemplo.com"
                      className={formErrors.email ? 'error' : ''}
                    />
                    <span className="form-error">{formErrors.email || ''}</span>
                  </div>

                  <div className="form-group">
                    <label htmlFor="usuario-senha">
                      Senha {editingId && '(deixe em branco para manter)'}
                    </label>
                    <input
                      id="usuario-senha"
                      type="password"
                      value={formSenha}
                      onChange={(e) => setFormSenha(e.target.value)}
                      placeholder="Mínimo 8 caracteres"
                      className={formErrors.senha ? 'error' : ''}
                    />
                    <span className="form-error">{formErrors.senha || ''}</span>
                  </div>

                  <div className="form-group">
                    <label htmlFor="usuario-tipo">Tipo</label>
                    <select
                      id="usuario-tipo"
                      value={formTipo}
                      onChange={(e) => setFormTipo(e.target.value)}
                      className={formErrors.tipo ? 'error' : ''}
                    >
                      <option value="">Selecione...</option>
                      <option value="Cliente">Cliente</option>
                      <option value="Arquiteto">Arquiteto</option>
                      <option value="Assistente">Assistente</option>
                    </select>
                    <span className="form-error">{formErrors.tipo || ''}</span>
                  </div>
                </div>

                <div className="usuarios-modal__footer">
                  <button type="button" className="btn-secondary" onClick={closeModal}>
                    Cancelar
                  </button>
                  <button type="submit" className="btn-primary" disabled={submitting}>
                    {submitting ? 'Salvando...' : editingId ? 'Salvar Alterações' : 'Cadastrar'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Confirmation Modal */}
        {confirmModal && (
          <div className="usuarios-modal-overlay" onClick={() => setConfirmModal(null)}>
            <div className="confirm-modal" onClick={(e) => e.stopPropagation()}>
              <h3>
                {confirmModal.action === 'excluir'
                  ? 'Confirmar Exclusão'
                  : `Confirmar ${confirmModal.action === 'ativar' ? 'Ativação' : 'Desativação'}`}
              </h3>
              <p>
                {confirmModal.action === 'excluir'
                  ? <>Deseja excluir permanentemente o usuário <strong>{confirmModal.usuario.nome}</strong>? Esta ação não pode ser desfeita.</>
                  : <>Deseja {confirmModal.action} o usuário <strong>{confirmModal.usuario.nome}</strong>?</>}
              </p>
              <div className="confirm-modal-actions">
                <button className="btn-secondary" onClick={() => setConfirmModal(null)}>
                  Cancelar
                </button>
                <button
                  className={confirmModal.action === 'excluir' ? 'btn-primary btn-danger' : 'btn-primary'}
                  onClick={confirmModal.action === 'excluir' ? confirmExcluir : confirmToggleStatus}
                >
                  {confirmModal.action === 'excluir' ? 'Excluir' : 'Confirmar'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default UsuariosCRUD;
